import {useHalCollection, useHalState} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {LigneMatching} from 'api-types/ffconnect';
import {PanierDesc} from 'api-types/ffconnect-hal';
import {useMemo} from 'react';

/**
 * Import Pdf :
 * Données du processus d'import PDF à l'origine d'un panier, pour l'UI du
 * panier en mode commercial : badge « PDF », en-tête « Panier - Commande PDF »,
 * bandeau d'alertes et ordre des lignes.
 *
 * Le lien `processus` du panier signe le panier PDF, et suffit à autoriser
 * l'UI : il est absent d'un panier ordinaire, et retiré à la sérialisation pour
 * qui n'a pas la permission `voir-les-processus-pdf-commande`, que le client
 * n'a pas. Le lire sur le panier, déjà chargé, allume l'UI sans attendre la
 * requête du processus.
 *
 * Le processus chargé, lui, n'est retenu que s'il correspond au `processus_id`
 * du panier affiché : au changement d'onglet haligator conserve le state
 * précédent le temps du rechargement, et sans cette garde on lirait celui du
 * panier d'avant — jusqu'au fichier ouvert par « Voir le fichier PDF ».
 */

export interface MatchingPdf {
  parLignePanier: ReadonlyMap<string, LigneMatching>; // ligne_panier_id -> ligne du PDF dont elle provient
  sansLignePanier: LigneMatching[]; // lignes du PDF n'ayant pas produit de ligne de panier
}

export const usePanierPdf = (panierNav: Navigable<PanierDesc>) => {
  const panierHook = useHalState(panierNav);
  const processusHook = useHalState(panierNav.follow('processus'));

  // Unique critère "d'allumage" de l'UI PDF
  const isPanierPdf = panierHook.state?.hasOneLink('processus') ?? false;

  // Le processus en cache peut être celui de l'onglet précédent : haligator
  // garde le state en place le temps du rechargement. On ne le lit donc que
  // s'il est bien celui que le panier affiché réclame.
  const processusIdAttendu = panierHook.data?.processus_id;
  const processusState =
    processusIdAttendu && processusIdAttendu === processusHook.state?.data.id
      ? processusHook.state
      : undefined;

  // Récupération des étapes.
  const etapesHook = useHalCollection(processusState?.follow('etapes'));

  // Même garde que pour le processus, et pour la même raison : le processus
  // arrive avant ses étapes, donc entre les deux le hook porte encore celles
  // de l'onglet précédent. On renvoie `items` tel quel plutôt qu'un tableau
  // filtré, pour ne pas invalider à chaque rendu les mémos qui en dépendent.
  const etapes =
    processusIdAttendu &&
    etapesHook.items?.every(
      (etape) => etape.data.processus_id === processusIdAttendu,
    )
      ? etapesHook.items
      : undefined;

  // Un panier PDF dont on attend encore les étapes : l'en-tête réserve alors la
  // place des chips. La question est bien « ai-je fini d'attendre » et non « ai-je
  // des compteurs » — un processus sans correspondances n'en produira jamais, et
  // sa place resterait réservée indéfiniment.
  const chargementPdf = isPanierPdf && etapes === undefined;

  // Recuperation du rapport de matching
  const rapportMatch = useMemo(() => {
    const etape = etapes?.find((etape) => etape.data.type === 'match');
    return etape?.data.rapport?.type === 'match'
      ? etape.data.rapport
      : undefined;
  }, [etapes]);

  const rapportPanier = useMemo(() => {
    const etape = etapes?.find((etape) => etape.data.type === 'panier');
    return etape?.data.rapport?.type === 'panier'
      ? etape.data.rapport
      : undefined;
  }, [etapes]);

  // ligne_panier_id -> numero_ligne_pdf, écrit à la création et à la fusion du
  // panier : c'est le seul lien entre une ligne de panier et sa ligne du PDF
  // pour le classement des articles dans le panier
  const ordreLignesPdf = useMemo(
    () =>
      new Map(
        rapportPanier?.correspondances?.map((correspondance) => [
          correspondance.ligne_panier_id,
          correspondance.numero_ligne_pdf,
        ]) ?? [],
      ),
    [rapportPanier],
  );

  // Jointure des deux rapports : à quelle ligne du PDF correspond chaque ligne
  // du panier, et quelles lignes du PDF n'y sont pas représentées. `undefined`
  // hors panier PDF, et sur un processus antérieur aux correspondances (sans
  // ce repli, toutes ses lignes passeraient pour non représentées).
  const matchingPdf = useMemo<MatchingPdf | undefined>(() => {
    if (!rapportMatch || !rapportPanier?.correspondances) return undefined;
    // On indexe les lignes du matching par id
    const lignesMatchingParId = new Map(
      rapportMatch.lignes.map((ligne) => [ligne.id, ligne]),
    );
    // Jointure ligne de panier ↔ ligne de matching.
    const parLignePanier = new Map<string, LigneMatching>();
    for (const correspondance of rapportPanier.correspondances) {
      const ligneMatching = lignesMatchingParId.get(
        correspondance.ligne_matching_id,
      );
      if (
        ligneMatching &&
        !parLignePanier.has(correspondance.ligne_panier_id)
      ) {
        parLignePanier.set(correspondance.ligne_panier_id, ligneMatching);
      }
    }

    // Lignes du PDF qui n'ont pas produit de ligne de panier :
    // soit le matching n'a trouvé aucun article,
    // soit l'article matché n'était plus disponible au catalogue à la transformation.
    // Elles n'existent que dans le rapport, il faudra les injecter dans la liste affichée.
    const idsLignesMatching = new Set(
      rapportPanier.correspondances.map(
        (correspondance) => correspondance.ligne_matching_id,
      ),
    );
    const sansLignePanier = rapportMatch.lignes.filter(
      (ligne) => !idsLignesMatching.has(ligne.id),
    );

    return {
      parLignePanier,
      sansLignePanier,
    };
  }, [rapportPanier, rapportMatch]);

  return {
    ordreLignesPdf,
    rapportMatch,
    isPanierPdf,
    chargementPdf,
    processus: processusState,
    matchingPdf,
  };
};
